import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";

import ReservationsSetting from "./parts/ReservationsSetting";
import UIButton from "@reusables/UIButton";

import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useUpdateCompanyDefaultsMutation } from "@redux/features/companies/companiesApi";
import { setSettings } from "@redux/features/auth/authSlice";
import { toastError } from "@helpers/utils";
import { isErrorWithMessage } from "@redux/api/query";

const defaultsScheme = z.object({
    enable_reservations: z.boolean()
});

type DefaultsFormTyping = z.infer<typeof defaultsScheme>;

export default function DefaultsPage() {
    const { t } = useTranslation("", { keyPrefix: "settings.general.defaults" });

    const dispatch = useAppDispatch();

    const company = useAppSelector(state => state.auth.user?.company);
    const companySettings = company?.settings;

    const [updateDefaults, { isLoading }] = useUpdateCompanyDefaultsMutation();

    const form = useForm<DefaultsFormTyping>({
        resolver: zodResolver(defaultsScheme),
        defaultValues: {
            enable_reservations: companySettings?.enable_reservations ?? false
        }
    });

    const { isDirty } = form.formState;

    // Sync form with company settings when they change
    useEffect(() => {
        if (companySettings) {
            form.reset({
                enable_reservations: companySettings.enable_reservations ?? false
            });
        }
    }, [companySettings, form]);

    const onSubmit = form.handleSubmit(async (data) => {
        if (!company?.id) return;

        try {
            const updatedDefaults = await updateDefaults({
                id: company.id,
                ...data
            }).unwrap();

            dispatch(setSettings(updatedDefaults));
            toast.success(t("responses.success"));

            form.reset(data);
        } catch (e) {
            if (isErrorWithMessage(e)) {
                toastError(e);
            } else {
                console.error("An error occurred while updating defaults:", e);
            }
        }
    });

    return (
        <FormProvider {...form}>
            <div className="levitation-extended">
                <div className="flex flex-row justify-between items-center mb-[44px]">
                    <div className="font-bold text-2xl text-accent">{t("heading")}</div>
                </div>
                <div className="mb-[32px]">
                    <ReservationsSetting />
                </div>

                <div className="text-center">
                    <UIButton
                        className="w-[200px]"
                        size={"lg"}
                        onClick={onSubmit}
                        disabled={!isDirty}
                        loading={isLoading}
                    >
                        {t("buttons.save")}
                    </UIButton>
                </div>
            </div>
        </FormProvider>
    );
}
